<template>
  <div class="service-detail-list">
    <h1>{{ $t(labels.title) }}</h1>
    <ServiceDetailSearch
      :show-periods="false"
      @textToSearch="textToSearchReceived"
      @selectAll="selectAll"
      @filterByStatus="filterByStatus"
      @filterByPeriod="filterByPeriod"
      @sortBy="sortByReceived"
    />
    <div
      v-if="!trucks && textToSearch.length == 0"
      class="loader"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>

    <div
      v-if="noSearchResults && !loading"
      class="no-results"
    >
      <v-icon>mdi-truck</v-icon>
      <br>{{ $t('service_details.txt.empty_search') }}
    </div>

    <div
      v-else-if="noSearchResults && loading"
      class="loader"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>
    <div
      v-else-if="trucks && trucks.length > 0"
      class="results"
    >
      <div class="results_table">
        <v-row class="header">
          <v-col
            cols="1"
            class="pl-5"
          >
            <v-checkbox
              v-model="checkSelected"
              class="check-item"
              :disabled="!checkSelected"
              :ripple="false"
              @change="setSelectedTrucks"
            />
          </v-col>
          <v-col
            cols="6"
            sm="4"
            md="2"
          >
            {{ $t('service_details.txt.vehicle_name') }}
          </v-col>
          <v-col
            sm="4"
            md="2"
            class="d-none d-sm-block"
          >
            {{ $t('service_details.txt.license_plate') }}
          </v-col>
          <v-col
            sm="3"
            md="2"
            class="d-none d-sm-block"
          >
            {{ $t('service_details.txt.activated') }}
          </v-col>
          <v-col
            md="2"
            class="d-md-block"
          >
            {{ $t('service_details.txt.type') }}
          </v-col>
        </v-row>
        <v-row class="body">
          <v-col
            v-for="vehicle in visibleItemsPerPage"
            :key="vehicle.subscriptionID"
            cols="12"
            :items="visibleItemsPerPage"
            class="item py-1"
          >
            <v-card
              class="pa-5"
              :class="{ selected: selected.includes(vehicle) }"
              :disabled="selected.length > 0 && (selected[0].type != vehicle.type ||
                (filterStatus.key === 'inactive' && selected[0].billingPeriod != vehicle.billingPeriod)) || disabledVehicleInCart(vehicle.subscriptionNumber)"
            >
              <v-container
                fluid
                class="pa-0"
              >
                <v-row>
                  <v-col
                    cols="1"
                    class="align-self-center d-flex align-center"
                  >
                    <v-checkbox
                      v-model="selected"
                      class="check-item d-flex"
                      :disabled="cannotSelectVehicle(vehicle)"
                      :value="vehicle"
                      :ripple="false"
                      @change="setSelectedTruck"
                    />

                    <v-tooltip
                      v-if="!vehicle.hasTablet"
                      class="no-tablet-warn"
                      content-class="vehicle-name-tooltip"
                      bottom
                      color="slate"
                    >
                      <template v-slot:activator="{ on }">
                        <v-icon
                          width="18"
                          color="orange"
                          v-on="on"
                        >
                          mdi-alert
                        </v-icon>
                      </template>
                      <span> {{ $t('service_details.txt.no_tablet_available') }}</span>
                    </v-tooltip>

                    <v-tooltip
                      v-if="!vehicle.hasInVehicleAppBase"
                      class="no-tablet-warn"
                      content-class="vehicle-name-tooltip"
                      bottom
                      color="slate"
                    >
                      <template v-slot:activator="{ on }">
                        <v-icon
                          width="18"
                          color="orange"
                          v-on="on"
                        >
                          mdi-alert
                        </v-icon>
                      </template>
                      <span> {{ $t('service_details.txt.no_basePack_available') }}</span>
                    </v-tooltip>
                  </v-col>
                  <v-col
                    v-if="vehicle.vin || vehicle.truckName"
                    cols="11"
                    sm="4"
                    :md="2"
                    class="align-self-center vehicle-name-ellipsis "
                  >
                    <v-tooltip
                      content-class="vehicle-name-tooltip"
                      bottom
                      color="slate"
                    >
                      <template v-slot:activator="{ on }">
                        <span
                          v-if="(vehicle.truckName === null || vehicle.truckName == '') && vehicle.vin"
                          class="vehicle-name"
                          @click="viewVehicleDetail(vehicle.subscriptionNumber)"
                          v-on="on"
                        >...{{ vehicle.vin.substring(vehicle.vin.length - 8) }}</span>
                        <span
                          v-else
                          class="vehicle-name"
                          @click="viewVehicleDetail(vehicle.subscriptionNumber)"
                          v-on="on"
                        >{{ vehicle.truckName }}</span>
                      </template>
                      <div>
                        <div>
                          <span> {{ $t('service_details.txt.vehicle_name') }}:</span> {{ vehicle.truckName }}
                        </div>
                        <div>
                          <span>{{ $t('service_details.txt.vin_number') }}:</span> {{ vehicle.vin }}
                        </div>
                      </div>
                    </v-tooltip>
                  </v-col>
                  <v-col
                    sm="4"
                    md="2"
                    class="d-none d-sm-block text-left align-self-center"
                  >
                    {{ vehicle.plateNumber }}
                  </v-col>
                  <v-col
                    sm="3"
                    md="2"
                    class="d-none d-sm-block text-left align-self-center"
                  >
                    {{ vehicle.activated | formatDate }}
                  </v-col>
                  <v-col
                    v-if="vehicle.type == 'Month'"
                    md="2"
                    class="d-none d-md-block text-left align-self-center"
                  >
                    {{ $t('service_details.txt.type_month') }}
                  </v-col>
                  <v-col
                    v-else-if="vehicle.type == 'Annual'"
                    md="2"
                    class="d-none d-md-block text-left align-self-center"
                  >
                    {{ $t('service_details.txt.type_annual') }}
                  </v-col>
                  <v-col
                    v-else-if="vehicle.type == 'Three Years' || vehicle.type == 'Three_Years'"
                    md="2"
                    class="d-none d-md-block text-left align-self-center"
                  >
                    {{ $t('service_details.txt.type_three_years') }}
                  </v-col>
                  <v-col
                    :md="vehicle.type != 'Month' && vehicle.type != 'Annual' && vehicle.type != 'Three Years' && vehicle.type != 'Three_Years' ? 5 : 3"
                    class="d-none d-sm-block text-right align-self-center"
                  >
                    <v-btn
                      v-if="!selected.includes(vehicle)"
                      class="show-button elevation-0"
                      @click="viewVehicleDetail(vehicle.subscriptionNumber)"
                    >
                      {{ $t('service_details.txt.show_vehicle') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </v-card>
          </v-col>
        </v-row>
      </div>
      <div class="results_pagination">
        <v-pagination
          v-model="page"
          color="pale"
          prev-icon="mdi-arrow-left"
          next-icon="mdi-arrow-right"
          :length="Math.ceil(filteredList.length / itemsPerPage)"
        />
      </div>
    </div>
    <v-footer
      fixed
      color="white"
      class="py-6 elevation-2"
    >
      <v-container class="py-0">
        <v-row class="results_footer">
          <v-col
            class="py-2 py-md-0 text-center text-sm-left align-self-center"
            cols="12"
            sm="4"
            md="2"
            lg="1"
            offset-lg="4"
          >
            <v-icon
              width="22"
              color="steel"
            >
              mdi-truck
            </v-icon>
            <span>
              x <b class="results_footer_costs">{{ selected.length }} </b>
            </span>
          </v-col>
          <v-col
            class="py-2 py-md-0 text-center text-sm-right align-self-center"
            cols="12"
            sm="8"
            md="6"
            lg="4"
          >
            <div v-if="filterStatus.key === 'active'">
              <span>
                <b class="results_footer_costs"> - {{ monthlyCost | formatPrice(currency.symbol) }}</b> {{ $t('service_details.txt.month') }}
              </span>
            </div>
            <div
              v-else
              class="results_footer_chooseperiod"
            >
              <v-bottom-navigation class="buttons elevation-0 align-center justify-sm-end">
                <span class="label">{{ $t('service_details.txt.choose_period') }}</span>
                <v-btn
                  v-model="activeBtn['month']"
                  class="elevation-0 ml-4 mr-2"
                  :disabled="selected.length === 0 || (selected.length > 0 && selected[0].billingPeriod == 'Yearly') || !(selectedHasMonthlyPlan.length > 0)"
                >
                  <span>
                    <b>{{ monthlyCost | formatPrice(currency.symbol) }}</b> {{ $t('service_details.txt.month') }}
                  </span>
                </v-btn>
                <v-icon class="d-none d-md-block">
                  mdi-chevron-right
                </v-icon>
              </v-bottom-navigation>
            </div>
          </v-col>
          <v-col
            class="py-2 py-md-0 text-center text-md-right align-self-center"
            cols="12"
            md="4"
            lg="3"
          >
            <v-btn
              v-if="filterStatus.key === 'active'"
              class="results_footer_btn"
              :class="items.length == 0 ? 'disabled' : 'cancel'"
              :disabled="items.length == 0"
              @click="bulkStopService()"
            >
              {{ $t('service_details.txt.cancel') }}
            </v-btn>
            <v-btn
              v-else
              class="results_footer_btn"
              :class="disabledButton || items.length == 0 ? 'disabled' : 'proceed'"
              :disabled="disabledButton || items.length == 0"
              @click="bulkActiveService()"
            >
              {{ $t('service_details.txt.proceed') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-footer>
  </div>
</template>

<script>
import apiCalls from '@/helpers/apiCall'
import api from '@/helpers/api'
import ServiceDetailSearch from '@/components/ServiceDetailSearch.vue'
import { mapState, mapMutations, mapActions } from 'vuex'
import vhlAppService from '@/helpers/vehicleAppService'

export default {
  name: 'VehicleAppsDetailList',
  components: {
    ServiceDetailSearch
  },
  props: {
    service: {
      type: Object,
      required: true
    },
    inVehicleAppCatalog: {
      type: [Array, Object],
      required: true,
      default: () => []
    },
    allTrucks: {
      type: [Array, Object],
      required: true,
      default: () => []
    }
  },
  data() {
    return {
      productID: '',
      page: 1,
      pageCount: 0,
      itemsPerPage: 8,
      trucks: [],
      serviceInfo: [],
      selected: [],
      textToSearch: '',
      filterStatus: {
        key: 'inactive',
        text: this.$t('service_details.txt.inactive')
      },
      filterPeriod: null,
      selectedBy: '',
      sortBy: {
        key: 'activated',
        text: ''
      },
      checkSelected: false,
      activeBtn: {},
      notification: false,
      notificationText: '',
      notificationType: 'success',
      loading: false,
      mutuatedInVehicleApp: []
    }
  },
  computed: {
    ...mapState('account', ['currency', 'payload']),
    ...mapState('checkout', ['checkout']),
    selectedHasYearlyPlan() {
      const itemsWithAPlan = this.selected.filter(item => {
        const itemAnnual = item.totalCost.filter(cost => {
          return cost.servicePeriod === 'Annual'
        })
        return itemAnnual.length > 0
      })
      return itemsWithAPlan
    },
    selectedHasMonthlyPlan() {
      const itemsWithAPlan = this.selected.filter(item => {
        const itemAnnual = item.totalCost.filter(cost => {
          return cost.servicePeriod === 'Monthly'
        })
        return itemAnnual.length > 0
      })
      return itemsWithAPlan
    },
    noSearchResults() {
      return (
        (this.trucks && this.trucks.length == 0) ||
        (this.textToSearch.length > 0 && this.filteredList.length == 0) ||
        this.filteredList.length == 0
      )
    },
    disabledButton() {
      // if (
      //   Object.keys(this.activeBtn).length === 0 ||
      //   (!this.activeBtn.annual && !this.activeBtn.month)
      // )
      //   return true
      // return false
      return false
    },
    monthlyCost() {
      const cost = this.items
        .filter(item => item.monthlyCost)
        .reduce((acc, item) => acc + item.monthlyCost, 0)
      if (this.filterStatus.key === 'active') {
        const activeCost = cost * -1
        this.$emit('monthlyCost', activeCost)
      } else {
        // if (this.activeBtn.month) {
        //   this.$emit('monthlyCost', cost)
        // } else {
        //   this.$emit('monthlyCost', 0)
        // }
        this.$emit('monthlyCost', cost)
      }
      return cost
    },
    yearlyCost() {
      const cost = this.items
        .filter(item => item.yearlyCost)
        .reduce((acc, item) => acc + item.yearlyCost, 0)
      if (this.filterStatus.key === 'active') {
        const activeCost = cost * -1
        this.$emit('yearlyCost', activeCost)
      } else {
        if (this.activeBtn.annual) {
          this.$emit('yearlyCost', cost)
        } else {
          this.$emit('yearlyCost', 0)
        }
      }
      return cost
    },
    labels() {
      return {
        title: vhlAppService.isThirdPartyApp(this.service)
          ? 'vehicleApps_details.txt.third_party_apps_vehicles'
          : 'vehicleApps_details.txt.vehicles'
      }
    },
    filteredList() {
      const IsNotYearlyVehicle = vehicle => vehicle.billingPeriod != 'Yearly'
      const HasTablet = vehicle => vehicle.HasTablet == true || true
      const MatchesSearch = vehicle => {
        const clearSearch = this.textToSearch.trim()
        if (!clearSearch) return true

        const searchPatt = new RegExp(RegExp.escape(clearSearch), 'i')
        return (
          (vehicle.truckName && searchPatt.test(vehicle.truckName)) ||
          (vehicle.plateNumber && searchPatt.test(vehicle.plateNumber)) ||
          (vehicle.vin && searchPatt.test(vehicle.vin)) ||
          (vehicle.type && searchPatt.test(vehicle.type)) ||
          (vehicle.activated && searchPatt.test(vehicle.activated))
        )
      }

      const IsActive = v => v.activated
      const IsInactive = v => !v.activated

      const filterActionList = [IsNotYearlyVehicle, HasTablet, MatchesSearch]

      if (this.filterStatus.key === 'active') {
        filterActionList.push(IsActive)
      } else {
        filterActionList.push(IsInactive)
      }

      const filteredVehicles = this.trucks.filter(vehicle =>
        filterActionList.every(ac => ac(vehicle))
      )

      filteredVehicles.sort(this.vehicleSorter(this.sortBy.key))

      return filteredVehicles
    },

    items() {
      const items = this.selected
      const array = []
      items.forEach(i => {
        this.trucks.forEach(tr => {
          if (tr.subscriptionID === i.subscriptionID) {
            if (tr.type === 'Month') {
              tr.totalCost &&
                tr.totalCost.forEach(cost => {
                  if (cost.servicePeriod === 'Monthly') {
                    cost.servicePrices.forEach(sp => {
                      if (sp.currency === this.currency.code) {
                        array.push({
                          subscriptionNumber: tr.subscriptionNumber,
                          productRateplanID: cost.productRatePlanId,
                          rateplanID: tr.ratePlanID,
                          productRateplanChargeID: cost.prodRatePlanChargeId,
                          type: tr.type,
                          monthlyCost: sp.price
                        })
                      }
                    })
                  }
                })
            } else if (tr.type === 'Annual') {
              tr.totalCost &&
                tr.totalCost.forEach(cost => {
                  if (cost.servicePeriod === 'Annual') {
                    cost.servicePrices.forEach(sp => {
                      if (sp.currency === this.currency.code) {
                        array.push({
                          subscriptionNumber: tr.subscriptionNumber,
                          productRateplanID: cost.productRatePlanId,
                          rateplanID: tr.ratePlanID,
                          productRateplanChargeID: cost.prodRatePlanChargeId,
                          type: tr.type,
                          yearlyCost: sp.price
                        })
                      }
                    })
                  }
                })
            } else if (tr.type === 'Three Years' || tr.type === 'Three_Years') {
              tr.totalCost &&
                tr.totalCost.forEach(cost => {
                  if (cost.servicePeriod === 'Three Years') {
                    cost.servicePrices.forEach(sp => {
                      if (sp.currency === this.currency.code) {
                        array.push({
                          subscriptionNumber: tr.subscriptionNumber,
                          productRateplanID: cost.productRatePlanId,
                          rateplanID: tr.ratePlanID,
                          productRateplanChargeID: cost.prodRatePlanChargeId,
                          type: tr.type,
                          yearlyCost: sp.price
                        })
                      }
                    })
                  }
                })
            } else {
              tr.totalCost &&
                tr.totalCost.forEach(cost => {
                  if (cost.servicePeriod === 'Monthly') {
                    cost.servicePrices.forEach(sp => {
                      if (sp.currency === this.currency.code) {
                        array.push({
                          subscriptionNumber: tr.subscriptionNumber,
                          productRateplanID: cost.productRatePlanId,
                          rateplanID: tr.ratePlanID,
                          productRateplanChargeID: cost.prodRatePlanChargeId,
                          type: 'Month',
                          monthlyCost: sp.price
                        })
                      }
                    })
                  } else if (cost.servicePeriod === 'Annual') {
                    cost.servicePrices.forEach(sp => {
                      if (sp.currency === this.currency.code) {
                        array.push({
                          subscriptionNumber: tr.subscriptionNumber,
                          productRateplanID: cost.productRatePlanId,
                          rateplanID: tr.ratePlanID,
                          productRateplanChargeID: cost.prodRatePlanChargeId,
                          type: 'Annual',
                          yearlyCost: sp.price
                        })
                      }
                    })
                  }
                })
            }
          }
        })
      })
      return array
    },

    visibleItemsPerPage() {
      return this.filteredList.slice(
        (this.page - 1) * this.itemsPerPage,
        this.page * this.itemsPerPage
      )
    },
    vehicleInCart() {
      const array = []
      const x = this.checkout
        ? this.checkout.filter(x => x.productID == this.productID)
        : false
      if (x && x.length > 0) {
        x.forEach(product => {
          product.rateplans.forEach(rp => array.push(...rp.subscriptions))
        })
      }
      return array
    }
  },
  watch: {
    items() {
      this.monthlyCost
      this.yearlyCost
    },
    selected(newValue) {
      this.activeBtn.month = newValue.length > 0
    }
  },
  async created() {
    this.productID = this.$router.history.current.params.id
    await this.getVehicles()
  },
  methods: {
    ...mapMutations('checkout', ['addTotalCheckout', 'setCheckout']),
    ...mapActions('notification', ['setSnackbar']),
    ...mapActions('trucks', ['invalidateTrucksCache']),
    getVehicles() {
      this.loading = true
      return apiCalls
        .get(api.TrucksPerServiceDetail, this.productID, '')
        .then(res => {
          this.loading = false
          if (res.status == 200) {
            this.serviceInfo = res.data
            const sortedTrucks = this.checkBaseProduct(res.data.trucks)
            this.trucks = sortedTrucks
          } else {
            this.serviceInfo = []
            this.trucks = []
          }

          this.onTrucksLoaded()
        })
    },
    checkBaseProduct(data) {
      const vehicleBaseProduct = this.inVehicleAppCatalog.filter(res => {
        return res.isBaseProduct
      })
      const isAddingBase = this.service.isBaseProduct
      data.forEach(result => {
        const filteredTruck = this.allTrucks.filter(filteredRes => {
          return filteredRes.vin__c === result.vin
        })
        filteredTruck.forEach(truckRes => {
          truckRes.ratePlans.forEach(rpRes => {
            vehicleBaseProduct.forEach(vehicleRes => {
              vehicleRes.productRatePlans.forEach(prpRes => {
                if (
                  this.checkout &&
                  this.checkout !== undefined &&
                  this.checkout.length > 0
                ) {
                  this.checkout.forEach(res => {
                    res.rateplans.forEach(cartRps => {
                      if (cartRps.productRateplanID === prpRes.id) {
                        prpRes['isAddtoCart'] = true
                      } else {
                        if (prpRes['isAddtoCart'] === false) {
                          prpRes['isAddtoCart'] = false
                        }
                      }
                    })
                  })
                }
                if (
                  prpRes.id == rpRes.productRatePlanId ||
                  isAddingBase ||
                  prpRes['isAddtoCart']
                ) {
                  result['hasInVehicleAppBase'] = true
                } else {
                  if (result['hasInVehicleAppBase'] === false) {
                    result['hasInVehicleAppBase'] = false
                  }
                }
              })
            })
          })
        })
      })
      return data
    },
    setSelectedTrucks() {
      if (this.checkSelected) {
        this.selected = this.visibleItemsPerPage.filter(
          item =>
            !this.vehicleInCart.some(
              cartItem => cartItem.subscriptionID === item.subscriptionNumber
            )
        )
      } else {
        this.selected = []
      }
    },
    setSelectedTruck() {
      if (this.selected.length == 0) this.checkSelected = false
      else this.checkSelected = true
    },
    filterByStatus(status) {
      this.selected = []
      this.checkSelected = false
      this.filterStatus = status
      this.filterPeriod = null
    },

    filterByPeriod(period) {
      if (this.selectedBy !== period.key) {
        this.selected = []
        this.checkSelected = false
      }
      this.filterPeriod = period
    },

    textToSearchReceived(search) {
      return (this.textToSearch = search)
    },
    sortByReceived(sortBy) {
      return (this.sortBy = sortBy)
    },
    viewVehicleDetail(subscriptionNumber) {
      this.$router.push({
        name: 'vehicle_details',
        params: { id: subscriptionNumber }
      })
    },
    disabledVehicleInCart(subscriptionNumber) {
      return this.vehicleInCart.some(
        x => x.subscriptionID == subscriptionNumber
      )
    },
    bulkStopService() {
      this.items.forEach(item => {
        apiCalls
          .put(api.CartAdd, '', '', {
            subscriptionID: item.subscriptionNumber,
            productRateplanID: item.productRateplanID,
            rateplanID: item.rateplanID,
            productRateplanChargeID: item.productRateplanChargeID,
            productID: this.productID,
            addSubscription: 0,
            metaData: { '': '' }
          })
          .then(res => {
            if (res.status == 200) {
              this.setCheckout(res.data.products)
              this.invalidateTrucksCache()
              this.addTotalCheckout(this.items.length)
              this.setSnackbar({
                showing: true,
                type: 'success',
                text: this.$t('service_details.txt.notification_cancel')
              })
              this.selected = []
            } else {
              this.setSnackbar({
                showing: true,
                type: 'error',
                text: this.$t('service_details.txt.notification_error')
              })
            }
          })
      })
    },
    bulkActiveService() {
      // if (this.activeBtn.annual == true) {
      //   const itemsToAdd = []
      //   this.items.forEach(item => {
      //     if (item.type === 'Annual') {
      //       const itemToAdd = {
      //         subscriptionID: item.subscriptionNumber,
      //         productRateplanID: item.productRateplanID,
      //         rateplanID: '',
      //         productRateplanChargeID: item.productRateplanChargeID,
      //         productID: this.productID,
      //         addSubscription: 1,
      //         metaData: { '': '' }
      //       }
      //       itemsToAdd.push(itemToAdd)
      //     }
      //   })
      //   apiCalls.put(api.CartBulkAdd, '', '', itemsToAdd).then(res => {
      //     if (res.status == 200) {
      //       this.setCheckout(res.data.products)
      //       this.addTotalCheckout(this.selected.length)
      //       this.setSnackbar({
      //         showing: true,
      //         type: 'success',
      //         text: this.$t('service_details.txt.notification_addcart')
      //       })
      //       this.selected = []
      //       this.activeBtn.annual = false
      //     } else {
      //       this.setSnackbar({
      //         showing: true,
      //         type: 'error',
      //         text: this.$t('service_details.txt.notification_error')
      //       })
      //     }
      //   })
      // } else if (this.activeBtn.month == true) {
      const itemsToAdd = []
      this.items.forEach(item => {
        if (item.type === 'Month') {
          const itemToAdd = {
            subscriptionID: item.subscriptionNumber,
            productRateplanID: item.productRateplanID,
            rateplanID: '',
            productRateplanChargeID: item.productRateplanChargeID,
            productID: this.productID,
            addSubscription: 1,
            metaData: { '': '' }
          }
          itemsToAdd.push(itemToAdd)
        }
      })
      apiCalls.put(api.CartBulkAdd, '', '', itemsToAdd).then(res => {
        if (res.status == 200) {
          this.setCheckout(res.data.products)
          this.invalidateTrucksCache()
          this.addTotalCheckout(this.selected.length)
          this.setSnackbar({
            showing: true,
            type: 'success',
            text: this.$t('service_details.txt.notification_addcart')
          })
          this.selected = []
          this.activeBtn.month = false
          this.checkSelected = false
        } else {
          this.setSnackbar({
            showing: true,
            type: 'error',
            text: this.$t('service_details.txt.notification_error')
          })
        }
      })
      // }
    },
    vehicleSorter(sortyByKey) {
      let targetProp = ''
      switch (sortyByKey) {
        case 'vinNumber':
          targetProp = 'vin'
          break

        case 'licensePlate':
          targetProp = 'plateNumber'
          break

        case 'activated':
          targetProp = 'activated'
          break

        case 'type':
          targetProp = 'type'
          break
      }
      if (!targetProp) return new Function()

      return (vehicle1, vehicle2) => {
        if (vehicle1[targetProp] < vehicle2[targetProp]) return -1
        if (vehicle1[targetProp] > vehicle2[targetProp]) return 1
        return 0
      }
    },
    selectAll() {
      this.selected = this.filteredList.filter(
        vehicle =>
          this.cannotSelectVehicle(vehicle) == false &&
          !this.disabledVehicleInCart(vehicle.subscriptionNumber)
      )
      this.checkSelected = this.selected.length > 0
    },
    onTrucksLoaded() {
      const activeCount = this.trucks.filter(vehicle => vehicle.activated)
        .length

      this.$emit('activeVehiclesCount', activeCount)
    },
    cannotSelectVehicle(vehicle) {
      const isAddonWithoutBaseProduct =
        !this.service.isBaseProduct && !vehicle.hasBaseProduct

      return (
        isAddonWithoutBaseProduct ||
        !vehicle.hasTablet ||
        !vehicle.hasInVehicleAppBase
      )
    }
  }
}
</script>

<style lang="scss">
.service-detail-list {
  .loader {
    text-align: center;
    padding-top: 48px;
  }
  h1 {
    margin-bottom: 22px;
    font-size: 3rem;
    font-weight: 300;
    line-height: normal;
    color: var(--v-dark-base);
    text-align: left;
  }
  .no-results {
    font-size: 1.375rem;
    line-height: normal;
    text-align: center;
    color: var(--v-bluegrey-base);
    .v-icon {
      font-size: 100px;
      color: var(--v-cloudy-base);
    }
  }
  .results {
    &_table {
      margin-bottom: 32px;
      .header {
        padding: 0 12px;
        [class*='col-'] {
          text-transform: uppercase;
          font-size: 0.8125rem;
          color: var(--v-bluegrey-base);
          padding: 10px;
        }
        .check-item {
          .v-input--selection-controls__input {
            width: 16px;
            height: 16px;
            border-radius: 4px;
            background-color: var(--v-pale-three-base);

            i {
              color: var(--v-pale-three-base);
              caret-color: var(--v-pale-three-base);
            }
          }

          &.v-input--is-disabled {
            .v-input--selection-controls__input {
              opacity: 0.6;
              i {
                &:before {
                  content: '';
                }
              }
            }
          }
        }
      }
      .body {
        .item {
          .v-card {
            border-radius: 6px;
            box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.18);
            color: var(--v-slate-base);

            &.selected {
              background-color: var(--v-peacock-base);
              color: white;
              .v-input--selection-controls__input {
                background-color: var(--v-peacock-base) !important;
                i {
                  color: var(--v-pale-three-base) !important;
                }
              }
              .vehicle-name {
                color: white !important;
              }
            }

            .row {
              height: 56px;

              div[class*='col-'] {
                font-size: 0.875rem;
                &.vehicle-name-ellipsis {
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                }
                .vehicle-name {
                  cursor: pointer;
                  color: var(--v-peacock-base);
                  text-decoration: underline;
                }

                .check-item {
                  .v-input__slot {
                    margin: 0;
                    .v-input--selection-controls__input {
                      width: 16px;
                      height: 16px;
                      border-radius: 4px;
                      background-color: var(--v-pale-three-base);

                      i {
                        color: var(--v-pale-three-base);
                      }
                    }
                  }
                }
                .show-button {
                  font-size: 0.75rem;
                  font-weight: bold;
                  text-align: center;
                  color: var(--v-peacock-base);
                  height: 32px;
                  border-radius: 3px;
                  border: solid 1px rgba(0, 92, 169, 0.3);
                  background-color: white;
                }
              }
            }
          }
        }
      }
    }
    &_pagination {
      .v-pagination {
        &__item {
          font-weight: bold;
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
          margin: 0 8px;
          &:focus {
            outline: none !important;
          }
          &--active {
            color: var(--v-darkgrey-blue-base) !important;
            box-shadow: none;
          }
        }
        &__navigation {
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
          margin: 0 40px;
        }
        .v-icon {
          font-size: 1.25rem;
          color: var(--v-slate-base);
        }
      }
    }
    &_footer {
      &_btn {
        width: 100%;
        height: 48px !important;
        border-radius: 5px;
        font-weight: bold;
        text-align: center;
        &.proceed {
          background-image: linear-gradient(to bottom, #266cab 0%, #01529c);
          color: white;
        }
        &.cancel {
          background-image: linear-gradient(
            to bottom,
            #c53030 0%,
            #9b2c2c 100%
          );
          color: white;
        }
        &.disabled {
          background-color: var(--v-cloudy-base) !important;
          color: white !important;
        }
      }
      &_costs {
        font-size: 1.25rem;
        color: var(--v-slate-base);
      }
      &_chooseperiod {
        .label {
          font-size: 0.75rem;
          font-weight: bold;
          color: var(--v-peacock-base);
          text-transform: uppercase;
        }
        .buttons {
          .v-btn {
            width: 128px;
            height: 32px;
            border-radius: 3px;
            border: solid 1px var(--v-cloudy-base);
            background-color: white !important;
            font-size: 0.75rem;
            text-align: center;
            color: var(--v-steel-base);

            &.v-btn--active {
              background-color: var(--v-peacock-base) !important;
              color: white !important;
            }

            &.disabled {
              color: white !important;
            }
          }
        }
      }
    }
  }
}

.v-input--selection-controls {
  margin-top: 0px;
  padding-top: 0px;
}

.v-messages {
  min-height: 0px;
}

.vehicle-name-tooltip {
  min-height: 32px !important;
  border-radius: 16px !important;
  span {
    text-transform: uppercase;
    font-size: 0.75rem;
    font-weight: bold;
    line-height: normal;
    text-align: center;
    color: white;
  }
}
.no-tablet-warn {
  position: absolute;
  left: 4px !important;
}
</style>
